import { useReducer } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconCheck,
  IconChevronDown,
  IconDots,
  IconHistory,
  IconPencil,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopConfirmDialog } from "../../../components/app/DesktopConfirmDialog";
import { Button } from "../../../components/ui/Button";
import { usePopoverMenu } from "../../../components/ui/Popover";
import {
  DesktopMenu,
  DesktopMenuItem,
} from "../../../components/ui/DesktopMenu";
import { useConversationEngine } from "../../../domains/assistant/useConversationEngine";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { renderDayRelative } from "../../../domains/app/utils/renderDate";
import {
  useDeleteConversation,
  useDeleteConversations,
  useRenameConversation,
} from "../../../domains/gpt/hooks/useManageConversation";
import { useNewChatGeneration } from "../../../domains/gpt/stores/newChatGenerationStore";
import {
  assistantConversationListTitle,
  conversationListTitle,
} from "../../../domains/gpt/transcript";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useSingleFlightAction } from "../../../hooks/useSingleFlightAction";
import { createMemoryStore } from "../../../lib/store";
import { cn } from "../../../lib/utils";
import { useApiInfiniteQuery } from "../../../services/api";

type Sort = "newest" | "oldest";

/**
 * The rows the selection covers. `all` is every conversation the search `q`
 * lists, loaded on the page or not, but the unticked ones: the server resolves
 * it. It holds its search so a later edit of the field cannot widen it.
 */
type Selection =
  | { kind: "ids"; ids: readonly string[] }
  | {
      kind: "all";
      q: string | null;
      except: readonly string[];
      // When it was made: a conversation created later is not part of it.
      asOf: string;
    };

/** What the page is doing on top of listing: one interaction at a time. */
type PageState =
  | { mode: "browse" }
  | { mode: "rename"; id: string; draft: string; initial: string }
  | { mode: "confirm-one"; id: string; title: string }
  | { mode: "select"; selection: Selection }
  // The engine and the count are fixed when the dialog opens: the delete
  // resets the list, and the dialog must not follow it into its loading state.
  | {
      mode: "confirm-selection";
      selection: Selection;
      engine: "legacy" | "assistant";
      count: number;
    };

type PageAction =
  | { type: "browse" }
  | { type: "rename"; id: string; title: string }
  | { type: "draft"; draft: string }
  | { type: "ask-delete"; id: string; title: string }
  | { type: "select"; selection: Selection }
  | {
      type: "confirm-selection";
      engine: "legacy" | "assistant";
      count: number;
    };

function reducePage(state: PageState, action: PageAction): PageState {
  switch (action.type) {
    case "browse":
      return { mode: "browse" };
    case "rename":
      return {
        mode: "rename",
        id: action.id,
        draft: action.title,
        initial: action.title,
      };
    case "draft":
      if (state.mode !== "rename")
        throw new Error("should not happen: a draft outside a rename");
      return { ...state, draft: action.draft };
    case "ask-delete":
      return { mode: "confirm-one", id: action.id, title: action.title };
    case "select":
      return { mode: "select", selection: action.selection };
    case "confirm-selection":
      if (state.mode !== "select")
        throw new Error("should not happen: a confirmation with no selection");
      return {
        mode: "confirm-selection",
        selection: state.selection,
        engine: action.engine,
        count: action.count,
      };
    default:
      throw new Error(action satisfies never);
  }
}

// Kept across navigations: opening a conversation and coming back must find the
// search and the order as they were. Owned by the user who set them, so a later
// session in the same tab starts from the defaults.
const useHistoryFilters = createMemoryStore<{
  uid: string | null;
  search: string;
  sort: Sort;
}>({ uid: null, search: "", sort: "newest" });

const PAGE_SIZE = 20;

function firstPage<T>(bodies: readonly T[]): T {
  const first = bodies[0];
  if (first === undefined)
    throw new Error("should not happen: a loaded list has no page");
  return first;
}

/**
 * Every JuisciGPT conversation of the user, on desktop (Figma "Juisci GPT —
 * Historique"): searched, sorted, renamed in place, and deleted one at a time
 * or by selection. It lists the engine that answers new questions, like the
 * sidebar's recents.
 */
export default function DesktopGPTHistoryPage(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const { gptNewChatPageLink, gptConversationPageLink } = usePageLinks(null);
  const [, setNewChatGeneration] = useNewChatGeneration();
  const engine = useConversationEngine();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const deleteConversations = useDeleteConversations();
  const action = useSingleFlightAction();
  const isRunning = action.state.status === "running";
  const [state, dispatch] = useReducer(reducePage, { mode: "browse" });

  const uid = currentUser === null ? null : currentUser.uid;
  const [storedFilters, setFilters] = useHistoryFilters();
  const filters =
    storedFilters.uid === uid
      ? storedFilters
      : { uid, search: "", sort: "newest" as const };
  const query = useDebouncedValue(filters.search.trim(), 300);
  // The query these rows answer, not the one being typed: the debounce keeps
  // the previous result on screen for a beat.
  const searched = query === "" ? null : query;
  const listInput = {
    query:
      searched === null
        ? { limit: PAGE_SIZE, sort: filters.sort }
        : { limit: PAGE_SIZE, q: searched, sort: filters.sort },
  };
  const legacyQuery = useApiInfiniteQuery(
    "/webapp/private/conversations",
    engine === "legacy" ? listInput : false
  );
  const assistantQuery = useApiInfiniteQuery(
    "/assistant/conversations",
    engine === "assistant" ? listInput : false
  );

  const list = (() => {
    const now = Date.now();
    switch (engine) {
      // No user yet resolves to "pending" too: the sign-in gate is up.
      case "pending":
        return { status: "pending" as const };
      case "legacy":
        switch (legacyQuery.state) {
          case "idle":
            throw new Error("should not happen: the legacy list is idle");
          case "pending":
            return { status: "pending" as const };
          case "error":
            return { status: "error" as const };
          case "success":
            if (legacyQuery.total === undefined)
              throw new Error("should not happen: the history has no total");
            return {
              status: "success" as const,
              engine: "legacy" as const,
              total: legacyQuery.total,
              rows: legacyQuery.docs.map((conversation) => ({
                id: conversation.id,
                title: conversationListTitle(conversation, now),
                storedTitle: conversation.title,
                // The legacy list is ordered by creation.
                date: conversation.createdAt,
              })),
              onLoadMore: legacyQuery.canFetchNextPage
                ? legacyQuery.fetchNextPage
                : null,
              hasMore: legacyQuery.hasNextPage,
              listedAt: firstPage(legacyQuery.bodies).meta.listedAt,
            };
          default:
            throw new Error(legacyQuery satisfies never);
        }
      case "assistant":
        switch (assistantQuery.state) {
          case "idle":
            throw new Error("should not happen: the assistant list is idle");
          case "pending":
            return { status: "pending" as const };
          case "error":
            return { status: "error" as const };
          case "success":
            if (assistantQuery.total === undefined)
              throw new Error("should not happen: the history has no total");
            return {
              status: "success" as const,
              engine: "assistant" as const,
              total: assistantQuery.total,
              rows: assistantQuery.docs.map((conversation) => ({
                id: conversation.id,
                title: assistantConversationListTitle(conversation),
                storedTitle: conversation.title,
                // The assistant list dates a legacy row by its creation.
                date: conversation.sortDate,
              })),
              onLoadMore: assistantQuery.canFetchNextPage
                ? assistantQuery.fetchNextPage
                : null,
              hasMore: assistantQuery.hasNextPage,
              listedAt: firstPage(assistantQuery.bodies).meta.listedAt,
            };
          default:
            throw new Error(assistantQuery satisfies never);
        }
      default:
        throw new Error(engine satisfies never);
    }
  })();

  const selection = (() => {
    if (state.mode !== "select" && state.mode !== "confirm-selection")
      return null;
    // "All" for another search than the one listed selects nothing: the field
    // changed after the tick, and the rows it covered are no longer on screen.
    if (state.selection.kind === "all" && state.selection.q !== searched)
      return { kind: "ids" as const, ids: [] };
    return state.selection;
  })();
  // Counted against a loaded list only: while a new sort or search loads, the
  // selection has no total to count from, and Delete waits.
  const selectedCount =
    selection === null || list.status !== "success"
      ? 0
      : selection.kind === "all"
        ? list.total - selection.except.length
        : selection.ids.length;
  // A history with nothing in it, not a search that found nothing.
  const isEmpty =
    list.status === "success" && list.total === 0 && searched === null;

  function newConversation(): void {
    setNewChatGeneration((generation) => generation + 1);
    history.push(gptNewChatPageLink());
  }

  function toggleRow(id: string): void {
    if (selection === null)
      throw new Error("should not happen: a row toggled outside a selection");
    if (selection.kind === "all") {
      // Unticking a row of "all" excludes it, loaded rows or not.
      dispatch({
        type: "select",
        selection: {
          ...selection,
          except: selection.except.includes(id)
            ? selection.except.filter((other) => other !== id)
            : [...selection.except, id],
        },
      });
      return;
    }
    dispatch({
      type: "select",
      selection: {
        kind: "ids",
        ids: selection.ids.includes(id)
          ? selection.ids.filter((other) => other !== id)
          : [...selection.ids, id],
      },
    });
  }

  function saveRename(id: string, draft: string, initial: string): void {
    const title = draft.trim();
    if (title === "") return;
    // Enter on the untouched field names nothing: a conversation with no
    // stored title must not get its prefilled question written as one.
    if (title === initial.trim()) {
      dispatch({ type: "browse" });
      return;
    }
    void action.run(async () => {
      const done = await renameConversation(id, title);
      if (done) dispatch({ type: "browse" });
    });
  }

  function confirmDeleteOne(id: string): void {
    void action.run(async () => {
      const deleted = await deleteConversation(id);
      if (deleted !== null) dispatch({ type: "browse" });
    });
  }

  function confirmDeleteSelection(
    selected: Selection,
    listEngine: "legacy" | "assistant"
  ): void {
    void action.run(async () => {
      const done = await deleteConversations(
        listEngine,
        selected.kind === "all"
          ? {
              target: "all",
              q: selected.q,
              except: [...selected.except],
              asOf: selected.asOf,
            }
          : { target: "ids", ids: [...selected.ids] }
      );
      if (done) dispatch({ type: "browse" });
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-ds-canvas">
      <DesktopTopNav
        context={
          selection === null ? (
            <DesktopBreadcrumb
              // Back leads to JuisciGPT, the first crumb, like a conversation's:
              // the chevron and the crumb are one control.
              back={{
                kind: "first-crumb",
                onBack: () => {
                  history.replace(gptNewChatPageLink());
                },
              }}
              trail={[{ label: "JuisciGPT", to: gptNewChatPageLink() }]}
              current={t("History")}
            />
          ) : (
            <p className="m-0 font-dm-sans text-sm font-semibold text-ds-slate-700">
              {t("{{count}} selected", { count: selectedCount })}
            </p>
          )
        }
        trailing={{
          kind: "actions",
          content:
            selection === null ? (
              isEmpty || list.status !== "success" ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    dispatch({
                      type: "select",
                      selection: { kind: "ids", ids: [] },
                    });
                  }}
                >
                  {t("Select")}
                </Button>
              )
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    dispatch({ type: "browse" });
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={selectedCount <= 0}
                  onClick={() => {
                    if (list.status !== "success")
                      throw new Error(
                        "should not happen: a selection counted on no list"
                      );
                    dispatch({
                      type: "confirm-selection",
                      engine: list.engine,
                      count: selectedCount,
                    });
                  }}
                >
                  {t("Delete")}
                </Button>
              </div>
            ),
        }}
        surface="default"
        className="bg-ds-canvas"
      />
      <main className="min-h-0 flex-1 overflow-y-auto px-19 pt-8 pb-10">
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <span className="flex rounded-full bg-ds-mint-50 p-3 text-ds-mint-800">
              <IconHistory className="size-6" stroke={1.5} />
            </span>
            <h2 className="m-0 font-dm-sans text-desktop-h3 font-semibold text-ds-slate-950">
              {t("No conversations yet")}
            </h2>
            <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-500">
              {t("Your questions to JuisciGPT will appear here.")}
            </p>
            <Button
              type="button"
              variant="primary"
              size="md"
              className="mt-2"
              onClick={newConversation}
            >
              {t("New conversation")}
            </Button>
          </div>
        ) : (
          <div className="mx-auto flex w-full max-w-desktop-block flex-col gap-4">
            <div className="flex items-center gap-3">
              {selection === null || list.status !== "success" ? null : (
                <SelectAllControl
                  checked={list.total > 0 && selectedCount === list.total}
                  total={list.total}
                  onSelectAll={() => {
                    dispatch({
                      type: "select",
                      selection: {
                        kind: "all",
                        q: searched,
                        except: [],
                        // The server's time for the first page: the total
                        // the user sees is the one counted then.
                        asOf: list.listedAt,
                      },
                    });
                  }}
                  onClear={() => {
                    dispatch({
                      type: "select",
                      selection: { kind: "ids", ids: [] },
                    });
                  }}
                />
              )}
              <label className="flex w-105 items-center gap-2 rounded-desktop-row border border-ds-slate-200 bg-ds-white px-3 py-2.25 focus-within:border-ds-mint-800">
                <IconSearch
                  className="size-4 shrink-0 text-ds-slate-500"
                  stroke={1.5}
                />
                <input
                  type="search"
                  maxLength={200}
                  value={filters.search}
                  placeholder={t("Filter conversations")}
                  aria-label={t("Filter conversations")}
                  className="min-w-0 flex-1 border-none bg-transparent p-0 font-dm-sans text-sm text-ds-slate-950 outline-none placeholder:text-ds-slate-500"
                  onChange={(event) => {
                    setFilters({
                      uid,
                      search: event.target.value,
                      sort: filters.sort,
                    });
                  }}
                />
              </label>
              <span className="flex-1" />
              <SortMenu
                sort={filters.sort}
                onSort={(sort) => {
                  setFilters({ uid, search: filters.search, sort });
                }}
              />
            </div>

            {list.status === "pending" ? (
              <HistorySkeleton />
            ) : list.status === "error" ? (
              <p className="m-0 py-12 text-center font-dm-sans text-sm text-ds-slate-500">
                {t("Something went wrong. Please try again.")}
              </p>
            ) : (
              <>
                <p className="m-0 font-dm-sans text-xs font-medium text-ds-slate-700">
                  {searched === null
                    ? t("{{count}} conversations", { count: list.total })
                    : list.total === 0
                      ? t("No results for “{{query}}”", { query: searched })
                      : t("{{count}} results for “{{query}}”", {
                          count: list.total,
                          query: searched,
                        })}
                </p>
                {list.total === 0 && searched !== null ? (
                  <div className="flex flex-col items-center gap-2 border-t border-ds-slate-100 pt-12 text-center">
                    <p className="m-0 font-dm-sans text-ui-body font-semibold text-ds-slate-950">
                      {t("No conversation matches “{{query}}”", {
                        query: searched,
                      })}
                    </p>
                    <p className="m-0 font-dm-sans text-sm text-ds-slate-700">
                      {t(
                        "Try another word, or clear the search to see all your conversations."
                      )}
                    </p>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setFilters({ uid, search: "", sort: filters.sort });
                      }}
                    >
                      {t("Clear search")}
                    </Button>
                  </div>
                ) : (
                  <ul className="m-0 flex list-none flex-col p-0">
                    {list.rows.map((row) => (
                      <li key={row.id} className="border-b border-ds-slate-100">
                        {state.mode === "rename" && state.id === row.id ? (
                          <RenameRow
                            draft={state.draft}
                            disabled={isRunning}
                            onDraft={(draft) => {
                              dispatch({ type: "draft", draft });
                            }}
                            onSave={() => {
                              saveRename(row.id, state.draft, state.initial);
                            }}
                            onCancel={() => {
                              dispatch({ type: "browse" });
                            }}
                          />
                        ) : selection !== null ? (
                          <SelectableRow
                            title={row.title}
                            date={row.date}
                            checked={
                              selection.kind === "all"
                                ? !selection.except.includes(row.id)
                                : selection.ids.includes(row.id)
                            }
                            onToggle={() => {
                              toggleRow(row.id);
                            }}
                          />
                        ) : (
                          <BrowseRow
                            title={row.title}
                            date={row.date}
                            to={gptConversationPageLink(row.id)}
                            onRename={() => {
                              dispatch({
                                type: "rename",
                                id: row.id,
                                title:
                                  // The field holds 200 characters, as the
                                  // server does; an untitled row shows its
                                  // whole first question.
                                  row.storedTitle === null
                                    ? row.title.slice(0, 200)
                                    : row.storedTitle,
                              });
                            }}
                            onDelete={() => {
                              dispatch({
                                type: "ask-delete",
                                id: row.id,
                                title: row.title,
                              });
                            }}
                          />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {list.hasMore ? (
                  <div className="flex items-center justify-center gap-3 py-4">
                    <span className="font-dm-sans text-desktop-meta text-ds-slate-700">
                      {t("Showing {{shown}} of {{total}}", {
                        shown: list.rows.length,
                        total: list.total,
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={list.onLoadMore === null}
                      onClick={() => {
                        if (list.onLoadMore === null) return;
                        list.onLoadMore();
                      }}
                    >
                      {t("Load more")}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}
      </main>

      {state.mode === "confirm-one" ? (
        <DesktopConfirmDialog
          title={t("Delete conversation?")}
          confirmLabel={t("Delete")}
          note={t("This can't be undone.")}
          disabled={isRunning}
          confirmDisabled={false}
          onConfirm={() => {
            confirmDeleteOne(state.id);
          }}
          onCancel={() => {
            if (isRunning) return;
            dispatch({ type: "browse" });
          }}
        >
          <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
            {t("This will permanently delete this conversation:")}
          </p>
          <p className="m-0 font-dm-sans text-sm font-semibold text-ds-slate-950">
            {state.title}
          </p>
        </DesktopConfirmDialog>
      ) : null}

      {state.mode === "confirm-selection" && selection !== null ? (
        selection.kind === "all" &&
        selection.except.length === 0 &&
        selection.q === null ? (
          <DesktopConfirmDialog
            title={t("Delete all {{count}} conversations?", {
              count: state.count,
            })}
            confirmLabel={t("Delete all")}
            note={t("This can't be undone.")}
            disabled={isRunning}
            confirmDisabled={false}
            onConfirm={() => {
              confirmDeleteSelection(selection, state.engine);
            }}
            onCancel={() => {
              if (isRunning) return;
              dispatch({ type: "select", selection });
            }}
          >
            <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
              {t(
                "This will permanently delete your whole JuisciGPT history. Your collections, saved articles and visual abstracts are kept."
              )}
            </p>
          </DesktopConfirmDialog>
        ) : (
          <DesktopConfirmDialog
            title={t("Delete {{count}} conversations?", {
              count: state.count,
            })}
            confirmLabel={t("Delete")}
            note={t("This can't be undone.")}
            disabled={isRunning}
            // The search changed while the dialog was open: nothing is selected.
            confirmDisabled={
              selection.kind === "ids" && selection.ids.length === 0
            }
            onConfirm={() => {
              confirmDeleteSelection(selection, state.engine);
            }}
            onCancel={() => {
              if (isRunning) return;
              dispatch({ type: "select", selection });
            }}
          >
            <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
              {t(
                "This will permanently delete the {{count}} selected conversations.",
                { count: state.count }
              )}
            </p>
          </DesktopConfirmDialog>
        )
      ) : null}
    </div>
  );
}

const ROW_CLASS =
  "flex min-h-11 items-center gap-3.5 rounded-desktop-row px-3 py-2";

function RowText({
  title,
  date,
}: {
  title: string;
  date: string;
}): React.JSX.Element {
  return (
    <>
      <span className="min-w-0 flex-1 truncate font-dm-sans text-sm font-medium text-ds-slate-950">
        {title}
      </span>
      <span className="shrink-0 font-dm-sans text-desktop-meta whitespace-nowrap text-ds-slate-700">
        {renderDayRelative(date, new Date())}
      </span>
    </>
  );
}

/** A row that opens its conversation, with Rename and Delete behind "···". */
function BrowseRow({
  title,
  date,
  to,
  onRename,
  onDelete,
}: {
  title: string;
  date: string;
  to: string;
  onRename: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const menu = usePopoverMenu("right");
  return (
    <div
      className={cn(
        ROW_CLASS,
        "group hover:bg-ds-slate-50",
        menu.isOpen ? "bg-ds-slate-50" : null
      )}
    >
      <Link
        to={to}
        className="flex min-w-0 flex-1 items-center gap-3.5 no-underline"
      >
        <RowText title={title} date={date} />
      </Link>
      <button
        ref={menu.anchor.triggerRef}
        type="button"
        aria-label={t("More")}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        className={cn(
          "size-7 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0 text-ds-slate-950 hover:bg-ds-black/6",
          // Revealed on hover, kept while its menu is open or it has focus.
          menu.isOpen
            ? "flex"
            : "hidden group-focus-within:flex group-hover:flex"
        )}
        onClick={menu.toggle}
      >
        <IconDots className="size-4.5" stroke={1.5} />
      </button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className="w-48.5">
          <DesktopMenuItem
            description={null}
            icon={IconPencil}
            label={t("Rename")}
            trailing={null}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onRename();
              },
            }}
          />
          <DesktopMenuItem
            description={null}
            icon={IconTrash}
            label={t("Delete")}
            trailing={null}
            tone="danger"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onDelete();
              },
            }}
          />
        </DesktopMenu>
      ) : null}
    </div>
  );
}

/** A row of the multiple selection: the whole row toggles its checkbox. */
function SelectableRow({
  title,
  date,
  checked,
  onToggle,
}: {
  title: string;
  date: string;
  checked: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <label
      className={cn(
        ROW_CLASS,
        "cursor-pointer",
        checked ? "bg-ds-moss-100" : "hover:bg-ds-slate-50"
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        className="sr-only"
        onChange={onToggle}
      />
      <Checkbox checked={checked} />
      <RowText title={title} date={date} />
    </label>
  );
}

function Checkbox({ checked }: { checked: boolean }): React.JSX.Element {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-4.5 shrink-0 items-center justify-center rounded-sm",
        checked
          ? "bg-ds-mint-800 text-ds-white"
          : "border border-solid border-ds-slate-400 bg-ds-white"
      )}
    >
      {checked ? <IconCheck className="size-3" stroke={2.5} /> : null}
    </span>
  );
}

/** The checkbox ticks every listed conversation; the chevron spells it out. */
function SelectAllControl({
  checked,
  total,
  onSelectAll,
  onClear,
}: {
  checked: boolean;
  total: number;
  onSelectAll: () => void;
  onClear: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const menu = usePopoverMenu("right");
  return (
    <div className="flex items-center gap-1 rounded-lg border border-ds-slate-200 px-2 py-1.5">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={t("Select all")}
        className="flex cursor-pointer border-none bg-transparent p-0"
        onClick={checked ? onClear : onSelectAll}
      >
        <Checkbox checked={checked} />
      </button>
      <button
        ref={menu.anchor.triggerRef}
        type="button"
        aria-label={t("Selection options")}
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        className="flex cursor-pointer border-none bg-transparent p-0 text-ds-slate-700"
        onClick={menu.toggle}
      >
        <IconChevronDown className="size-3.5" stroke={1.5} />
      </button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className={null}>
          <DesktopMenuItem
            description={null}
            icon={null}
            label={t("Select all {{count}}", { count: total })}
            trailing={null}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onSelectAll();
              },
            }}
          />
          <DesktopMenuItem
            description={null}
            icon={null}
            label={t("Select none")}
            trailing={null}
            tone="default"
            action={{
              kind: "button",
              onClick: () => {
                menu.close();
                onClear();
              },
            }}
          />
        </DesktopMenu>
      ) : null}
    </div>
  );
}

function SortMenu({
  sort,
  onSort,
}: {
  sort: Sort;
  onSort: (sort: Sort) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const menu = usePopoverMenu("right");
  const labels = {
    newest: t("Sort: Newest"),
    oldest: t("Sort: Oldest"),
  } as const satisfies Record<Sort, string>;
  return (
    <>
      <button
        ref={menu.anchor.triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={menu.isOpen}
        className="flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-transparent py-2 pr-2.5 pl-3 font-dm-sans text-sm font-medium text-ds-slate-700 hover:bg-ds-black/4"
        onClick={menu.toggle}
      >
        {labels[sort]}
        <IconChevronDown className="size-3.5" stroke={1.5} />
      </button>
      {menu.isOpen ? (
        <DesktopMenu anchor={menu.anchor} className={null}>
          {(["newest", "oldest"] as const).map((option) => (
            <DesktopMenuItem
              description={null}
              key={option}
              icon={null}
              label={labels[option]}
              trailing={option === sort ? { kind: "check" } : null}
              tone="default"
              action={{
                kind: "button",
                onClick: () => {
                  menu.close();
                  onSort(option);
                },
              }}
            />
          ))}
        </DesktopMenu>
      ) : null}
    </>
  );
}

/** The title in an inline field: Enter saves, Escape puts the row back. */
function RenameRow({
  draft,
  disabled,
  onDraft,
  onSave,
  onCancel,
}: {
  draft: string;
  disabled: boolean;
  onDraft: (draft: string) => void;
  onSave: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={cn(ROW_CLASS, "bg-ds-slate-50")}>
      <input
        autoFocus
        maxLength={200}
        value={draft}
        disabled={disabled}
        aria-label={t("Conversation name")}
        // The DS Input at its desktop size (Figma "Input" md): focus darkens the
        // border, never a glow.
        className="h-10 min-w-0 flex-1 rounded-xl border border-solid border-ds-slate-400 bg-ds-white px-4 font-dm-sans text-ui-body text-ds-slate-950 outline-none"
        onChange={(event) => {
          onDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSave();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      />
      <span className="shrink-0 font-dm-sans text-desktop-meta whitespace-nowrap text-ds-slate-500">
        {t("↵ Save · Esc Cancel")}
      </span>
    </div>
  );
}

function HistorySkeleton(): React.JSX.Element {
  return (
    <ul className="m-0 flex list-none flex-col p-0" aria-hidden>
      {["w-2/3", "w-1/2", "w-3/4", "w-2/5", "w-3/5", "w-1/2"].map(
        (width, index) => (
          <li
            key={index}
            className="flex min-h-11 items-center gap-3.5 border-b border-ds-slate-100 px-3"
          >
            <span
              className={cn(
                "h-3.5 animate-pulse rounded bg-ds-slate-100",
                width
              )}
            />
          </li>
        )
      )}
    </ul>
  );
}
